<template>
  <div>
    <sales-type-list-add-new
      :is-add-new-sales-type-sidebar-active.sync="isAddNewSalesTypeSidebarActive"
      @refetch-data="refetchData"
    />
    <sales-type-list-edit
      :selected-sales-type-id="selectedSalesTypeIdForEdit"
      @refetch-data="refetchData"
      @updated="resetSelectedSalesTypeIdForEdit"
    />
    <sales-type-list-view
      :selected-sales-type-id="selectedSalesTypeIdForView"
      @close="resetSelectedSalesTypeIdForView"
    />

    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label>entries</label>
          </b-col>

          <!-- Search and Actions -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end control-bar">
              <b-form-input
                v-model="searchQuery"
                class="search-input"
                placeholder="Search..."
              />

              <!-- Watch Now Button -->
              <b-button
                variant="outline-success"
                class="watch-now-btn"
                @click="openWatchVideo"
              >
                <feather-icon icon="PlayIcon" size="16" class="mr-1" />
                <span class="watch-now-text">Watch Now</span>
              </b-button>

              <!-- Export Button (Hidden) -->
              <!--
              <b-button
                variant="outline-secondary"
                class="export-btn"
                @click="exportSalesTypes"
                :disabled="isExporting"
              >
                <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
                {{ isExporting ? 'Exporting...' : 'Export' }}
              </b-button>
              -->

              <b-button variant="primary" class="add-btn" @click="isAddNewSalesTypeSidebarActive = true">
                <span class="text-nowrap">Add Sales Type</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refSalesTypeListTable"
        class="position-relative"
        :items="fetchSalesTypes"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="No matching records found"
        :busy="isTableLoading"
        sticky-header="true"
      >
        <!-- Loading Skeleton -->
        <template #table-busy>
          <table-skeleton :columns="tableColumns" :rows="5" />
        </template>
        
        <!-- Column: S.No -->
        <template #cell(sno)="data">
          <span class="text-muted">{{ data.value }}</span>
        </template>
        
        <!-- Column: ID -->
        <template #cell(id)="data">
          <span>{{ data.value }}</span>
        </template>

        <!-- Column: Name -->
        <template #cell(name)="data">
          <div class="d-flex">
            <h6 class="mb-0">{{ data.value }}</h6>
          </div>
        </template>

        <!-- Column: Code -->
        <template #cell(code)="data">
          <span
            class="font-weight-bold text-primary cursor-pointer hover-effect"
            @click="viewSalesType(data.item.id)"
            title="Click to view Sales Type details"
          >
            {{ data.value || '-' }}
          </span>
        </template>

        <!-- Column: Prefix -->
        <template #cell(prefix)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Status -->
        <template #cell(isActive)="data">
          <b-badge :variant="getStatusVariant(data.value)" pill class="status-badge">
            {{ data.value ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <div class="d-flex align-items-center justify-content-center">
            <!-- View Button -->
            <b-button
              variant="flat-primary"
              class="btn-icon rounded-circle"
              title="View Sales Type"
              @click="viewSalesType(data.item.id)"
            >
              <feather-icon icon="EyeIcon" size="16" />
            </b-button>

            <!-- Edit Button -->
            <b-button
              variant="flat-info"
              class="btn-icon rounded-circle"
              title="Edit Sales Type"
              @click="editSalesType(data.item.id)"
            >
              <feather-icon icon="EditIcon" size="16" />
            </b-button>

            <!-- Delete Button -->
            <b-button
              variant="flat-danger"
              class="btn-icon rounded-circle"
              title="Delete Sales Type"
              @click="onDeleteSalesType(data.item.id)"
            >
              <feather-icon icon="TrashIcon" size="16" />
            </b-button>
          </div>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalSalesTypes"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import store from '@/store';
import { onUnmounted, ref } from '@vue/composition-api';
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BFormInput,
  BLink,
  BMedia,
  BPagination,
  BRow,
  BTable,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import salesTypeStoreModule from './SalesTypeStoreModule';
import SalesTypeListAddNew from './SalesTypeListAddNew.vue';
import SalesTypeListEdit from './SalesTypeListEdit.vue';
import SalesTypeListView from './SalesTypeListView.vue';
import useSalesTypeList from './useSalesTypeList';
import TableSkeleton from '@/components/common/skeletons/TableSkeleton.vue';

export default {
  components: {
    'sales-type-list-add-new': SalesTypeListAddNew,
    'sales-type-list-edit': SalesTypeListEdit,
    'sales-type-list-view': SalesTypeListView,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,

    vSelect,
    TableSkeleton,
  },
  setup(props, { root }) {
    const SALES_TYPE_APP_STORE_MODULE_NAME = 'fbr-invoice-sales-type';

    // Register module
    if (!store.hasModule(SALES_TYPE_APP_STORE_MODULE_NAME))
      store.registerModule(SALES_TYPE_APP_STORE_MODULE_NAME, salesTypeStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SALES_TYPE_APP_STORE_MODULE_NAME))
        store.unregisterModule(SALES_TYPE_APP_STORE_MODULE_NAME);
    });

    const isAddNewSalesTypeSidebarActive = ref(false);
    const selectedSalesTypeIdForEdit = ref(null);
    const selectedSalesTypeIdForView = ref(null);
    const isExporting = ref(false);

    // Video link for Watch Now button
    const salesTypeVideoLink = 'https://cdn.cloudious.net/file-1756984083546-400157862.mp4';

    // Watch video function
    const openWatchVideo = () => {
      window.open(salesTypeVideoLink, '_blank');
    };

    const resetSelectedSalesTypeIdForEdit = () => {
      selectedSalesTypeIdForEdit.value = null;
    };

    const resetSelectedSalesTypeIdForView = () => {
      selectedSalesTypeIdForView.value = null;
    };

    const viewSalesType = (id) => {
      selectedSalesTypeIdForView.value = id;
      // The view sidebar will open automatically
    };

    const editSalesType = (id) => {
      selectedSalesTypeIdForEdit.value = id;
      // The edit sidebar will open automatically
    };

    const onDeleteSalesType = async (id) => {
      try {
        // Show confirmation dialog using root.$bvModal
        const confirmed = await new Promise((resolve) => {
          root.$bvModal
            .msgBoxConfirm('Are you sure you want to delete this Sales Type?', {
              title: 'Please Confirm',
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'danger',
              okTitle: 'YES',
              cancelTitle: 'NO',
              footerClass: 'p-2',
              hideHeaderClose: false,
              centered: true,
            })
            .then((value) => resolve(value));
        });

        if (!confirmed) return;

        // Call delete action
        await store.dispatch('fbr-invoice-sales-type/deleteSalesType', { id });

        // Show success toast
        root.$bvToast.toast('Sales Type deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });

        // Refetch data to update the table
        refetchData();
      } catch (error) {
        console.error('Error deleting sales type:', error);

        // Show user-friendly error message
        let errorMessage = 'Error deleting Sales Type. Please try again.';

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        // Show error toast
        root.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      }
    };

    const exportSalesTypes = async () => {
      isExporting.value = true;
      try {
        const response = await store.dispatch('fbr-invoice-sales-type/exportSalesTypes');

        // Create download link
        const blob = new Blob([response.data], {
          type:
            response.headers['content-type'] ||
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sales_types_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        root.$bvToast.toast('Sales types exported successfully', {
          title: 'Export successful!',
          variant: 'success',
          solid: true,
        });
      } catch (error) {
        console.error('Export error:', error);
        root.$bvToast.toast('Failed to export sales types', {
          title: 'Export failed',
          variant: 'danger',
          solid: true,
        });
      } finally {
        isExporting.value = false;
      }
    };

    const {
      fetchSalesTypes,
      tableColumns,
      perPage,
      currentPage,
      totalSalesTypes,
      dataMeta,
      perPageOptions,
      searchQuery,
      refSalesTypeListTable,
      refetchData,
      isTableLoading,
    } = useSalesTypeList();

    // Helper function for status variant
    const getStatusVariant = (isActive) => {
      return isActive ? 'light-success' : 'light-danger';
    };

    return {
      // Sidebar
      isAddNewSalesTypeSidebarActive,
      selectedSalesTypeIdForEdit,
      selectedSalesTypeIdForView,

      tableColumns,
      perPage,
      currentPage,
      totalSalesTypes,
      dataMeta,
      perPageOptions,
      searchQuery,
      refSalesTypeListTable,

      fetchSalesTypes,
      refetchData,
      resetSelectedSalesTypeIdForEdit,
      resetSelectedSalesTypeIdForView,
      viewSalesType,
      editSalesType,
      onDeleteSalesType,
      exportSalesTypes,
      isExporting,
      isTableLoading,

      // Helper functions
      getStatusVariant,
      openWatchVideo,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

// Skeleton loader styles
.skeleton-row {
  .skeleton-cell {
    padding: 0.75rem;

    .skeleton-item {
      height: 20px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
      border-radius: 4px;
    }
  }
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Enhanced badge styling for status
.status-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  transition: all 0.2s ease-in-out;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

// Sticky header styles
.b-table-sticky-header {
  max-height: 80vh !important;
}

// Clickable code styling
.cursor-pointer {
  cursor: pointer;
}

.hover-effect {
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #0056b3 !important;
    text-decoration: underline;
    transform: translateY(-1px);
  }
}

// Light variant badge styles
.badge.badge-pill {
  &.light-success {
    background-color: #d1f2eb;
    color: #0f5132;
    border: 1px solid #badbcc;
  }

  &.light-danger {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
}

// Button styling improvements
.watch-now-btn {
  min-width: 140px;
  height: 42px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  border-width: 1.5px;
  padding: 0.5rem 1rem;
  
  .watch-now-text {
    white-space: nowrap;
    display: inline-block;
    vertical-align: middle;
  }
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
    border-width: 2px;
  }
  
  // Ensure proper spacing between icon and text
  .feather-icon {
    margin-right: 0.5rem;
  }
}

.export-btn {
  min-width: 110px;
  height: 42px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  border-width: 1.5px;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
    border-width: 2px;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.add-btn {
  min-width: 160px;
  height: 42px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease-in-out;
  padding: 0.5rem 1rem;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  }
}

// Responsive button layout
@media (max-width: 768px) {
  .watch-now-btn,
  .export-btn,
  .add-btn {
    min-width: auto;
    width: 100%;
    margin-bottom: 0.5rem;
    height: 44px; // Slightly larger on mobile for better touch targets
    
    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Additional button enhancements
.watch-now-btn,
.export-btn,
.add-btn {
  border-radius: 6px;
  font-family: inherit;
  letter-spacing: 0.025em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  // Consistent icon sizing
  .feather-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
}



// Control bar container styling
.control-bar {
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
}

// Search input styling to match buttons
.search-input {
  height: 42px;
  border-radius: 6px;
  border: 1.5px solid #d8d6de;
  transition: all 0.2s ease-in-out;
  min-width: 200px;
  padding: 0.5rem 0.75rem;
  
  &:focus {
    border-color: #7367f0;
    box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
